import FormCardContainer from '@/components/container/FormCardContainer';
import { OnboardingStep, type BaseResponse, type OnboardingProgramDto } from '@/types';
import { Stepper, type StepperRefAttributes } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { useRef, useState } from 'react';
import EstimatedBudgetAllocationForm from './forms/EstimatedBudgetAllocationForm';
import ServicesToProvideForm from './forms/ServicesToProvideForm';
import RideProgramSuggestionsForm from './forms/RideProgramSuggestionsForm';
import { axiosInstance } from '@/services/axios.service';
import { AxiosResponse } from 'axios';
import { ENDPOINTS } from '@/constants/endpoints';
import { ChooseProgramWizardProps } from './accordion-content/ChooseProgramContent';
import { useQuery } from '@tanstack/react-query';
import { GenericResponse } from '@/common/common_utils';
import { useOnboardingState } from '../engine/onboarding.hooks';

export type OnBoardingStateProgramProps = {
	onboardingProgram: OnboardingProgramDto;
	isLoading: boolean;
	onSubmit: (data: Partial<OnboardingProgramDto>) => Promise<void>;
	onBack?: () => void;
}

const ChooseProgramWizard = (props: ChooseProgramWizardProps) => {
	const stepperRef = useRef<StepperRefAttributes>(null);
	const { completeStepMutation } = useOnboardingState();
	const [onboardingProgram, setOnboardingProgram] = useState<OnboardingProgramDto>({} as OnboardingProgramDto);

	const programQ = useQuery({
		queryKey: ['Onboarding.Program'],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<OnboardingProgramDto>>(ENDPOINTS['/api/X4bUser/GetOnboardingProgram'], {});
			setOnboardingProgram(result.data.Data);
			return result.data.Data;
		}
	});

	const onSubmit = async (
		data: Partial<OnboardingProgramDto>,
	) => {
		const activeStep = stepperRef.current?.getActiveStep() ?? 0;
		if ((activeStep) < steps.length - 1) {
			setOnboardingProgram({ ...onboardingProgram, ...data });
			stepperRef.current?.nextCallback();
		} else {
			if (!programQ.data?.Id) {
				// save 'program' part to backend
				await axiosInstance.post<
					BaseResponse,
					AxiosResponse<BaseResponse>,
					OnboardingProgramDto
				>(ENDPOINTS['/api/X4bUser/CreateOnboardingProgram'], onboardingProgram);
				await completeStepMutation.mutateAsync(OnboardingStep.ChooseProgram)
			} else {
				// save 'program' part to backend
				await axiosInstance.post<
					BaseResponse,
					AxiosResponse<BaseResponse>,
					OnboardingProgramDto
				>(ENDPOINTS['/api/X4bUser/UpdateOnboardingProgram'], onboardingProgram);
			}

			props.onClose();
		}
	};

	const steps = [
		{
			id: 'services-to-provide',
			content: (
				<ServicesToProvideForm
					onboardingProgram={onboardingProgram}
					isLoading={programQ.isLoading}
					onSubmit={onSubmit}
					onBack={() => {
						stepperRef.current?.prevCallback();
						props.onClose();
					}}
				/>
			),
		},

		{
			id: 'estimated-budget-allocation',
			content: (
				<EstimatedBudgetAllocationForm
					onboardingProgram={onboardingProgram}
					isLoading={programQ.isLoading}
					// // TODO: Implement validation
					// values={{
					// 	estimatedBudget: program.estimatedBudget ?? '',
					// 	estimatedFrequency: program.estimatedFrequency ?? '',
					// }}
					onSubmit={onSubmit}
					onBack={() => stepperRef.current?.prevCallback()}
				/>
			),

		},
		{
			id: 'program-suggestions',
			content: (
				<RideProgramSuggestionsForm
					onboardingProgram={onboardingProgram}
					isLoading={programQ.isLoading}
					onSubmit={onSubmit}
					onBack={() => stepperRef.current?.prevCallback()}
				/>
			),

		},
	];
	const currentStep = stepperRef.current?.getActiveStep?.() ?? 0;
	const totalSteps = steps.length;
	return (
		<FormCardContainer>
			<Stepper ref={stepperRef}>
				{steps.map((item) => (
					<StepperPanel key={item.id}>
						{/* <div className={cardStyles.container1200px}> */}
						{item.content}
						{/* </div> */}
					</StepperPanel>
				))}
			</Stepper>
		</FormCardContainer>
	);
};

export default ChooseProgramWizard;
